import { ref } from 'vue'

import { CAPTCHA_SITE_KEY } from '@/apps/adok/shared/config/product'

interface CaptchaWidget {
  execute: () => void
  reset: () => void
}

/**
 * Stands in for a token nobody can produce here. Worded to be unmistakable in a log or a
 * network tab: it is not a key, not a secret, and not valid anywhere the backend actually
 * asks Google.
 */
const DEV_PLACEHOLDER_TOKEN = 'dev-no-captcha-key'

const unchallengedToken = () =>
  import.meta.env.DEV ? DEV_PLACEHOLDER_TOKEN : ''

/**
 * Validate, spend a token, send with it — the three moves of a captcha-guarded form.
 *
 * **The order matters**: validating first means no challenge is put in front of the visitor
 * for a form the schema already refuses. A token is spent either way, so the widget is
 * reset after every attempt.
 *
 * **With no `VITE_CAPTCHA_KEY` there is no widget**, and `execute()` on one that was never
 * mounted does nothing and says nothing — so that case sends `DEV_PLACEHOLDER_TOKEN`
 * instead of waiting for a `verify` that cannot come. A placeholder and not `''`, which is
 * refused before the check that waves a local checkout through.
 *
 * It cannot loosen a deployment: `import.meta.env.DEV` is false in every built artefact, so
 * a build missing its key sends an empty token and fails loudly.
 */
export function useCaptchaGuard(options: {
  validate: () => boolean
  submit: (token: string) => unknown
}) {
  const captcha = ref<CaptchaWidget>()

  const enabled = CAPTCHA_SITE_KEY.length > 0

  const start = () => {
    if (!options.validate()) return

    if (enabled) captcha.value?.execute()
    else void options.submit(unchallengedToken())
  }

  const verified = async (token: string) => {
    await options.submit(token)
    captcha.value?.reset()
  }

  const expired = () => captcha.value?.reset()

  /**
   * Whether a challenge happens at all. A form does not care — `start` covers both cases
   * — but a caller that waits for the widget's `ready` before starting does: with no key
   * nothing is mounted and `ready` never comes.
   */
  return { captcha, enabled, start, verified, expired }
}
